import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';

const MFA_LENGTH = 6;

@Component({
  selector: 'app-mfa-modal',
  templateUrl: './mfa-modal.component.html',
  styleUrls: ['./mfa-modal.component.scss'],
  animations: [
    trigger('modalAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-20px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0, transform: 'translateY(-20px)' }))
      ])
    ])
  ]
})
export class MfaModalComponent implements OnChanges {
  @Input() isVisible = false;
  @Input() qrCodeUrl = '';
  @Input() errorMessageForm = '';
  @Input() userId: number | null = null;
  @Input() isLoading = false;
  @Output() close = new EventEmitter<void>();
  @Output() verify = new EventEmitter<string>();

  mfaDigits: string[] = Array(MFA_LENGTH).fill('');
  errorMessage = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isVisible']?.currentValue === true) {
      this.resetDigits();
    }
  }

  closeModal(): void {
    this.errorMessage = '';
    this.isVisible = false;
    this.close.emit();
    this.resetDigits();
  }

  verifyMfa(): void {
    if (this.isLoading) {
      return;
    }

    if (!this.isCodeComplete()) {
      this.errorMessage = 'MFA_MODAL_CODE_MESSAGE_ERROR';
      return;
    }

    this.errorMessage = '';
    this.verify.emit(this.mfaDigits.join(''));
  }

  onDigitKeydown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.verifyMfa();
      return;
    }

    if (event.key === 'Backspace') {
      if (this.mfaDigits[index]) {
        return;
      }
      if (index > 0) {
        event.preventDefault();
        this.mfaDigits[index - 1] = '';
        this.syncInput(index - 1);
        this.focusDigit(index - 1);
      }
    }
  }

  onDigitInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const digitsOnly = (input.value || '').replace(/\D/g, '');

    if (!digitsOnly) {
      this.mfaDigits[index] = '';
      input.value = '';
      this.errorMessage = '';
      return;
    }

    if (digitsOnly.length === 1) {
      this.mfaDigits[index] = digitsOnly;
      input.value = digitsOnly;
      this.errorMessage = '';

      if (index < MFA_LENGTH - 1) {
        this.focusDigit(index + 1);
      }
      return;
    }

    this.fillDigits(digitsOnly, index);
    this.errorMessage = '';
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') ?? '';
    this.fillDigits(pasted.replace(/\D/g, ''), 0);

    const nextEmpty = this.mfaDigits.findIndex(d => !d);
    this.focusDigit(nextEmpty === -1 ? MFA_LENGTH - 1 : nextEmpty);
  }

  isCodeComplete(): boolean {
    return this.mfaDigits.every(digit => digit !== '');
  }

  setLoading(loading: boolean): void {
    this.isLoading = loading;
  }

  setError(message: string): void {
    this.errorMessage = message;
    this.isLoading = false;
  }

  clearInputsOnInvalidCode(): void {
    this.resetDigits();
  }

  private fillDigits(value: string, startIndex: number): void {
    const chars = value.slice(0, MFA_LENGTH - startIndex).split('');
    chars.forEach((char, offset) => {
      this.mfaDigits[startIndex + offset] = char;
    });
    this.syncAllInputs();
  }

  private resetDigits(): void {
    this.mfaDigits = Array(MFA_LENGTH).fill('');
    this.errorMessage = '';
    this.isLoading = false;
    setTimeout(() => {
      this.syncAllInputs();
      this.focusDigit(0);
    }, 100);
  }

  private syncAllInputs(): void {
    for (let i = 0; i < MFA_LENGTH; i++) {
      this.syncInput(i);
    }
  }

  private syncInput(index: number): void {
    const el = document.getElementById(`mfa-digit-${index}`) as HTMLInputElement | null;
    if (el) {
      el.value = this.mfaDigits[index] || '';
    }
  }

  private focusDigit(index: number): void {
    const el = document.getElementById(`mfa-digit-${index}`) as HTMLInputElement | null;
    if (el) {
      el.focus();
      el.select();
    }
  }
}
